import React, { useMemo } from "react";
import PropTypes from "prop-types";

import Modal from "@shared/v2/Modal";
import Dropdown from "@shared/v2/Dropdown";

import TextInput from "@shared/v2/TextInput";
import PersonDetailError from "../PersonDetailError";
import { LOCALITY_LIST } from "../../shared/constants";

const STYLES = {
  errorClassName: "tw-absolute tw-bottom-[-20px]",
  textInputClassName: 'tw-flex-1 tw-relative'
}

// This component is used to render the body of a modal for adding or editing an address.
const AddEditModalBody = ({ addressTypes, setAddressType, addressType, setStreetAddress, streetAddress, city, setCity, setLocality, locality, setPostalCode, postalCode, error, specificTextInputError }) => {
  const stateToMatch = useMemo(
    () =>
      [].concat(
        ...LOCALITY_LIST.map((category) =>
          category.options.map((option) => ({ label: option.value, value: option.value })),
        ),
      ),
    [LOCALITY_LIST],
  );

  return (
    <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px]">
      {error && <PersonDetailError error={error} errorTitle={null} />}
      <div className="tw-flex tw-gap-[16px]">
        <Dropdown
          containerClassName="tw-w-[120px]"
          label="Address Type"
          options={addressTypes}
          onChange={(ad) => setAddressType(ad.value)}
          value={addressTypes.find((a) => a.value === addressType)}
          placeholder="Select"
        />

        <TextInput
          label="Street Address"
          containerClassName={STYLES.textInputClassName}
          onChange={(e) => setStreetAddress(e.target.value)}
          value={streetAddress}
          error={(() => {
            if (specificTextInputError?.streetAddress) {
              return specificTextInputError.streetAddress;
            }

            if (streetAddress.length > 75) {
              return "Max 75 characters.";
            }

            return null;
          })()}

          errorClassName={STYLES.errorClassName}
        />
      </div>
      <div className="tw-flex tw-gap-[16px]">
        <TextInput
          label="City"
          onChange={(e) => setCity(e.target.value)}
          value={city}
          containerClassName={STYLES.textInputClassName}
          errorClassName={STYLES.errorClassName}
          error={(() => {
            if (specificTextInputError?.city) {
              return specificTextInputError.city;
            }

            if (city.length > 75) {
              return "Max 75 characters.";
            }

            return null;
          })()}
        />
        <Dropdown
          containerClassName="tw-w-[128px]"
          label="State / Province"
          options={LOCALITY_LIST}
          onChange={(option) => setLocality(option?.value)}
          value={stateToMatch.find((o) => o.value === locality)}
          placeholder="Select"
          isSearchable
          menuShouldComeToFront
        />
        <TextInput
          label="ZIP / Postal Code"
          containerClassName='tw-w-[128px] tw-relative'
          onChange={(e) => setPostalCode(e.target.value)}
          value={postalCode}
          error={(() => {
            if (specificTextInputError?.postalCode) {
              return specificTextInputError.postalCode;
            }

            if (postalCode.length > 10) {
              return "Max 10 characters.";
            }

            return null;
          })()}
          errorClassName={STYLES.errorClassName}
        />
      </div>
    </Modal.Body>
  );
}

export default AddEditModalBody;

AddEditModalBody.propTypes = {
  addressTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAddressType: PropTypes.func.isRequired,
  addressType: PropTypes.string.isRequired,
  setStreetAddress: PropTypes.func.isRequired,
  streetAddress: PropTypes.string,
  city: PropTypes.string,
  setCity: PropTypes.func.isRequired,
  stateToMatch: PropTypes.shape({}).isRequired,
  setLocality: PropTypes.func.isRequired,
  locality: PropTypes.string,
  setPostalCode: PropTypes.func.isRequired,
  postalCode: PropTypes.string,
  error: PropTypes.string,
  specificTextInputError: PropTypes.shape({
    streetAddress: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
  }),
};

AddEditModalBody.defaultProps = {
  streetAddress: "",
  city: "",
  locality: "",
  postalCode: "",
  error: null,
  specificTextInputError: null,
};
